/**
 * Kickard — Ranked Season Table (permanent, season-driven).
 *
 * - Fetches GET /api/ranked/leaderboard?limit=100&guestId=<me> (unchanged).
 * - Hero reads the viewer's authoritative state from GET /api/ranked/me.
 * - All season copy is derived from the live `season` object, so the same
 *   screen renders PRESEASON TABLE / SEASON 1 TABLE / SEASON 2 TABLE …
 * - Server ordering is rendered as-is (no client re-sort).
 * - Phase 5.0B premium presentation (V7 asset pack).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadGuest } from '@/lib/guestIdentity';
import { usePlayerRowLinks } from '@/lib/playerProfileLink';
import { getRankedLeaderboard, getMyRankedState } from '@/api/rankedClient';
import {
  RankedBackdrop as Backdrop, RankedHeader, RankedTabs, SEASON_TABLE_ASSETS as A,
} from '../components/RankedChrome';
import { TIER_ART, crestNumeralStyle, CREST_PLACEHOLDER_STYLE } from '../lib/rankCrest';

// Home-parity rank crest map (duplicated verbatim from HomeScreen, same
// convention as MatchResultPremium / Scoreboard).
const DIVISION_ROMAN = { 1: 'I', 2: 'II', 3: 'III' };
const PILL_TIERS = ['bronze', 'silver', 'gold', 'platinum', 'diamond', 'master', 'champion', 'legend'];

export function seasonTitle(season) {
  return `${String(season?.name || 'Season').toUpperCase()} TABLE`;
}

export function seasonContext(season) {
  if (season?.name === 'Preseason') return ['Season resets', 'Before Season 1'];
  const d = season?.endsAt ? new Date(season.endsAt) : null;
  if (d && !isNaN(d.getTime())) {
    return ['Season ends', d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })];
  }
  return ['Season', 'Active'];
}

export function winRatePct(row) {
  const wins = row.wins || 0;
  const losses = row.losses || 0;
  const total = wins + losses;
  const pct = total > 0 ? Math.round((row.winRate ?? (wins / total)) * 100) : 0;
  return Math.max(0, Math.min(100, pct));
}

// Mirrors HomeScreen's strict "earned rank" semantics.
export function heroState(me) {
  if (!me || me.loading) return { kind: 'loading' };
  if (!me.data) return { kind: 'unknown' };
  const rank = me.data.rank || {};
  const placement = me.data.placement || {};
  const record = me.data.record || {};
  const tier = rank.currentTier ? String(rank.currentTier).toLowerCase() : null;
  const sr = rank.currentSR ?? null;
  const earned = !!(placement.placementComplete === true && tier && tier !== 'unranked'
    && sr != null && Number(record.rankedMatchesPlayed || 0) > 0);
  if (earned) {
    return { kind: 'ranked', tier, division: rank.currentDivision ?? null, label: rank.currentLabel || tier, sr };
  }
  if (placement.placementMatchesPlayed != null) {
    return {
      kind: 'placement',
      label: `Placement ${placement.placementMatchesPlayed}/${placement.placementMatchesRequired ?? 10}`,
    };
  }
  return { kind: 'unranked', label: 'Unranked' };
}

function TierChip({ tier, tierLabel, isPlacement, placementMatchesPlayed, placementMatchesRequired }) {
  const label = isPlacement
    ? `Placement ${placementMatchesPlayed || 0}/${placementMatchesRequired || 10}`
    : (tierLabel || 'Unranked');
  const variant = isPlacement ? 'placement' : (PILL_TIERS.includes(tier) ? tier : 'unranked');
  return (
    <span className={`kst-pill kst-pill--${variant}`} data-testid="leaderboard-tier-chip">{label}</span>
  );
}

function RankCell({ rank }) {
  if (rank >= 1 && rank <= 3) {
    return <img src={`${A}/medal_${rank}.webp`} alt={`Rank ${rank}`} className="kst__medal" data-testid={`leaderboard-medal-${rank}`} />;
  }
  return <span className="kst__rank-num">{rank}</span>;
}

export function LeaderboardRow({ row, isViewer }) {
  const top = row.rank >= 1 && row.rank <= 3;
  const pct = winRatePct(row);
  const fill = top ? (row.rank === 3 ? 'orange' : 'gold') : 'gray';
  const skin = top ? `kst__row--top kst__row--rank${row.rank}` : (isViewer ? 'kst__row--selected' : '');
  return (
    <div
      data-testid={isViewer ? 'leaderboard-row-viewer' : 'leaderboard-row'}
      data-guestid={row.guestId}
      data-rank={row.rank}
      className={`kst__row ${skin}`}
      role="button"
      tabIndex={0}
      aria-label={`Open ${row.displayName || 'Player'} profile`}
    >
      <div className="kst__cell kst__c-rank" data-testid="leaderboard-rank">
        <RankCell rank={row.rank} />
      </div>
      <div className="kst__cell kst__c-player">
        <div className="kst__name-line">
          <span className="kst__name" data-testid="leaderboard-name">{row.displayName || 'Player'}</span>
          {isViewer && <span className="kst-you" data-testid="leaderboard-you-chip">YOU</span>}
        </div>
        <TierChip
          tier={row.tier}
          tierLabel={row.tierLabel}
          isPlacement={row.isPlacement}
          placementMatchesPlayed={row.placementMatchesPlayed}
          placementMatchesRequired={row.placementMatchesRequired}
        />
      </div>
      <div className="kst__cell kst__c-wl">
        <span className="kst__wl" data-testid="leaderboard-wl">{row.wins || 0}W - {row.losses || 0}L</span>
        <div className="kst__bar" aria-hidden>
          <div className={`kst__bar-fill kst__bar-fill--${fill}`} style={{ width: `${pct}%` }} data-testid="leaderboard-wr-bar-fill" />
        </div>
      </div>
      <div className="kst__cell kst__c-wr">
        <span className="kst__wr" data-testid="leaderboard-wr">{pct}%</span>
      </div>
      <div className="kst__cell kst__c-sr">
        <span className="kst__sr" data-testid="leaderboard-sr">{row.currentSR || 0}</span>
        {top && <span className="kst__sr-unit">SR</span>}
      </div>
    </div>
  );
}

function HeroCrest({ hero }) {
  const ranked = hero.kind === 'ranked' && TIER_ART[hero.tier];
  const src = ranked ? TIER_ART[hero.tier] : TIER_ART.gold;
  const numeral = ranked && hero.tier !== 'legend' && hero.division ? DIVISION_ROMAN[Number(hero.division)] : null;
  return (
    <div className="kst__crest" data-testid="leaderboard-hero-crest" data-tier={ranked ? hero.tier : 'none'}>
      <img src={src} alt="" aria-hidden style={ranked ? undefined : CREST_PLACEHOLDER_STYLE} />
      {numeral && <span className="kst__crest-numeral" aria-hidden style={crestNumeralStyle(hero.tier)}>{numeral}</span>}
    </div>
  );
}

export function SeasonHero({ season, total, placementRequired, me }) {
  const hero = heroState(me);
  const [ctxTop, ctxBottom] = seasonContext(season);
  const label = hero.kind === 'loading' || hero.kind === 'unknown' ? '—' : hero.label;
  return (
    <section className="kst__hero" data-testid="leaderboard-hero">
      <div className="kst__hero-top">
        <HeroCrest hero={hero} />
        <div className="kst__hero-text">
          <div className="kst__eyebrow" data-testid="leaderboard-season-name">{String(season?.name || '').toUpperCase()}</div>
          <div className={`kst__hero-label${label.length > 11 ? ' kst__hero-label--long' : ''}`} data-testid="leaderboard-hero-label">{label}</div>
          {hero.kind === 'ranked' && (
            <div className="kst__hero-sr" data-testid="leaderboard-hero-sr">{hero.sr}<span>SR</span></div>
          )}
        </div>
        <img src={`${A}/crowned_ball_trophy.webp`} alt="" aria-hidden className="kst__trophy" />
      </div>
      <div className="kst__stats">
        <div className="kst__stat">
          <div className="kst__stat-value" data-testid="leaderboard-total">{total ?? '—'}</div>
          <div className="kst__stat-label">{total === 1 ? 'Player' : 'Players'}</div>
        </div>
        <div className="kst__stat">
          <div className="kst__stat-value" data-testid="leaderboard-placement-required">{placementRequired ?? '—'}</div>
          <div className="kst__stat-label">Placement matches</div>
        </div>
        <div className="kst__stat" data-testid="leaderboard-season-context">
          <div className="kst__stat-value kst__stat-value--text">{ctxTop}</div>
          <div className="kst__stat-label">{ctxBottom}</div>
        </div>
      </div>
    </section>
  );
}

export function SeasonTableView({ data, me, myGuestId, onBack, onNavigate }) {
  const { season, total, rows, viewerRow, placementMatchesRequired } = data;
  return (
    <Backdrop testid="ranked-leaderboard-screen">
      <div className="kst__inner">
        <RankedHeader
          title={seasonTitle(season)}
          subtitle="RANKED LEADERBOARD"
          onBack={onBack}
          backTestId="leaderboard-back"
          titleTestId="leaderboard-title"
        />

        <RankedTabs active="table" onNavigate={onNavigate} />

        <SeasonHero season={season} total={total} placementRequired={placementMatchesRequired} me={me} />

        <img src={`${A}/table_header.webp`} alt="# Player W-L Win % SR" className="kst__thead" />

        {rows.length === 0 ? (
          <div className="kst__empty" data-testid="leaderboard-empty">
            Nobody has played a ranked match yet.
            <br />Be the first on the board.
          </div>
        ) : (
          <div className="kst__rows" data-testid="leaderboard-rows">
            {rows.map((row) => (
              <LeaderboardRow
                key={row.guestId || row.rank}
                row={row}
                isViewer={!!myGuestId && row.guestId === myGuestId}
              />
            ))}
          </div>
        )}

        {viewerRow && (
          <>
            <div className="kst__divider">
              <span /><span className="kst__divider-label">Your position</span><span />
            </div>
            <LeaderboardRow row={viewerRow} isViewer={true} />
          </>
        )}
      </div>
    </Backdrop>
  );
}

export default function RankedLeaderboardScreen() {
  const navigate = useNavigate();
  usePlayerRowLinks(navigate);
  const location = useLocation();
  const [state, setState] = useState({ loading: true, data: null, err: null });
  const [me, setMe] = useState({ loading: true, data: null });
  const [retryTick, setRetryTick] = useState(0);

  // Origin-aware Back: when opened from a finalized Match Result, Back
  // returns to that SAME Match Result via the canonical read-only
  // `/match-summary/:matchId` route. Otherwise preserves the existing
  // browser-history Back behavior for Home / Profile / Competitive / direct URL entry.
  const handleBack = () => {
    const origin = location.state?.origin;
    const returnPath = location.state?.returnPath;
    if (origin === 'match_result' && typeof returnPath === 'string' && returnPath) {
      navigate(returnPath, { replace: true });
      return;
    }
    navigate(-1);
  };

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, err: null });
    (async () => {
      const g = loadGuest();
      const resp = await getRankedLeaderboard({
        limit: 100,
        guestId: g?.guestId || undefined,
      });
      if (cancelled) return;
      if (!resp.ok) {
        setState({ loading: false, data: null, err: resp.code || 'error' });
        return;
      }
      setState({ loading: false, data: resp, err: null });
    })();
    return () => { cancelled = true; };
  }, [retryTick]);

  // Viewer hero state — independent of the table; a failure here never
  // blocks the leaderboard and never fabricates a tier / SR.
  useEffect(() => {
    let cancelled = false;
    const gid = loadGuest()?.guestId;
    if (!gid) { setMe({ loading: false, data: null }); return undefined; }
    setMe({ loading: true, data: null });
    (async () => {
      const r = await getMyRankedState(gid);
      if (cancelled) return;
      setMe({ loading: false, data: r?.ok ? r : null });
    })();
    return () => { cancelled = true; };
  }, [retryTick]);

  const onRetry = () => setRetryTick((n) => n + 1);

  if (state.loading) {
    return (
      <Backdrop>
        <div className="kst__state k-safe-top">
          <div className="kst__state-msg" data-testid="leaderboard-loading">Loading leaderboard…</div>
        </div>
      </Backdrop>
    );
  }

  if (state.err === 'ranked_disabled') {
    return <UnavailableFrame msg="Ranked is currently unavailable." onBack={handleBack} testid="leaderboard-disabled" />;
  }
  if (state.err === 'no_active_season') {
    return <UnavailableFrame msg="No active season." onBack={handleBack} testid="leaderboard-no-season" />;
  }
  if (state.err) {
    // Covers transient Safari TypeError / network_error AFTER the
    // rankedClient retry wrapper has already exhausted its bounded
    // attempts. Expose a one-tap Retry so the user is not dead-ended.
    return (
      <UnavailableFrame
        msg="Couldn't load the leaderboard."
        onBack={handleBack}
        onRetry={onRetry}
        testid="leaderboard-error"
      />
    );
  }

  return (
    <SeasonTableView
      data={state.data}
      me={me}
      myGuestId={loadGuest()?.guestId}
      onBack={handleBack}
      onNavigate={(path) => navigate(path)}
    />
  );
}

function UnavailableFrame({ msg, onBack, onRetry, testid }) {
  return (
    <Backdrop testid={testid || 'leaderboard-unavailable'}>
      <div className="kst__state k-safe-top">
        <h1 className="kst__state-title">Season Table</h1>
        <p className="kst__state-msg">{msg}</p>
        <div className="kst__state-actions">
          {onRetry && (
            <button onClick={onRetry} className="kst__btn kst__btn--gold" data-testid="leaderboard-retry">
              Retry
            </button>
          )}
          <button onClick={onBack} className="kst__btn" data-testid="leaderboard-unavailable-back" aria-label="Back">
            Back
          </button>
        </div>
      </div>
    </Backdrop>
  );
}
